import React from 'react';

const defaultPricesUS = [
  { name: 'California', price: 4.89 },
  { name: 'Texas', price: 2.89 },
  { name: 'Florida', price: 3.29 },
  { name: 'New York', price: 3.69 },
  { name: 'Arizona', price: 3.19 },
  { name: 'Nevada', price: 3.99 },
  { name: 'Colorado', price: 3.09 },
  { name: 'Washington', price: 4.29 },
  { name: 'Oregon', price: 3.89 },
  { name: 'Utah', price: 3.15 },
];

const FuelPriceTable = ({
  darkMode,
  prices = defaultPricesUS,
  dollarRate = 4.12,
  lang = 'pl',
  isLoading = false,
  nationalAverage = null,
  lastUpdated = null,
}) => {
  const isPL = lang === 'pl';

  const tableHeaderClass = darkMode
    ? 'bg-slate-700 text-cyan-300'
    : 'bg-blue-50 text-blue-900';
  const tableRowClass = darkMode
    ? 'border-slate-700 hover:bg-slate-700'
    : 'border-slate-100 hover:bg-blue-50';

  const convertToPLNPerLiter = (usdPerGallon) => {
    return ((usdPerGallon * dollarRate) / 3.785).toFixed(2);
  };

  const getDiff = (price) => {
    if (nationalAverage === null) return null;
    return (price - nationalAverage).toFixed(2);
  };

  const displayPrices = prices.length > 0 ? prices : defaultPricesUS;

  return (
    <div className={`${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-100'} rounded-xl border shadow-lg overflow-hidden`}>
      <div className={`${darkMode ? 'bg-gradient-to-r from-blue-700 to-cyan-700' : 'bg-gradient-to-r from-blue-600 to-cyan-500'} px-6 py-4`}>
        <h3 className="text-white font-bold text-lg">
          {isPL ? 'Ceny paliwa w stanach USA' : 'Fuel Prices by US State'}
        </h3>
        <p className="text-blue-100 text-sm mt-1">
          {isPL ? 'Benzyna Regular – dane tygodniowe EIA' : 'Regular gasoline – EIA weekly data'}
        </p>
      </div>

      {isLoading ? (
        <div className="px-6 py-8 space-y-3">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className={`h-8 rounded animate-pulse ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}
            />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={tableHeaderClass}>
                <th className="px-6 py-3 text-left text-sm font-semibold">
                  {isPL ? 'Region' : 'Region'}
                </th>
                <th className="px-6 py-3 text-right text-sm font-semibold">
                  USD/galon
                </th>
                {nationalAverage !== null && (
                  <th className="px-6 py-3 text-right text-sm font-semibold">
                    {isPL ? 'vs. USA' : 'vs. USA'}
                  </th>
                )}
                <th className="px-6 py-3 text-right text-sm font-semibold">
                  {isPL ? 'PLN/litr' : 'PLN/liter'}
                </th>
              </tr>
            </thead>
            <tbody>
              {displayPrices.map((item, index) => {
                const diff = getDiff(item.price);
                return (
                  <tr
                    key={item.regionCode || item.name}
                    className={`${tableRowClass} border-b transition-colors ${index % 2 === 0 ? '' : darkMode ? 'bg-slate-800/50' : 'bg-slate-50/50'}`}
                  >
                    <td className={`px-6 py-3 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      {item.name || item.state}
                    </td>
                    <td className={`px-6 py-3 text-right font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      ${item.price.toFixed(2)}
                    </td>
                    {nationalAverage !== null && (
                      <td className={`px-6 py-3 text-right text-sm font-medium ${
                        diff > 0
                          ? 'text-red-500'
                          : diff < 0
                          ? 'text-green-500'
                          : darkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}>
                        {diff > 0 ? `+${diff}` : diff}
                      </td>
                    )}
                    <td className={`px-6 py-3 text-right font-medium ${darkMode ? 'text-cyan-400' : 'text-blue-600'}`}>
                      {convertToPLNPerLiter(item.price)} zł
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className={`px-6 py-3 ${darkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-50 text-slate-500'} text-xs flex flex-wrap justify-between gap-2`}>
        <span>
          {isPL
            ? `* Przeliczono po kursie ${dollarRate.toFixed(2)} PLN/USD. Ceny mogą się różnić.`
            : `* Converted at ${dollarRate.toFixed(2)} PLN/USD. Prices may vary.`}
        </span>
        {lastUpdated && (
          <span>
            {isPL ? `Dane z: ${lastUpdated}` : `Data as of: ${lastUpdated}`}
          </span>
        )}
      </div>
    </div>
  );
};

export default FuelPriceTable;
